import React, { ReactNode, useEffect, useRef, useState } from "react";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Paragraph from "atoms/typography/paragraph";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import ChatModalResults from "organisms/modals/chat-modal/chat-results";
import ChatPrompt from "organisms/modals/chat-modal/chat-prompt";
import { useConversationActions } from "utilities/atoms/conversations/current-conversation/use-conversation-actions.hook";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import ChatOfflineTooltipContent from "organisms/modals/chat-modal/chat-offline-tooltip-content";
import { ChatContextChip } from "molecules/chat-context/chat-context-chip";
import { ChatContextType } from "constants/chat-context-types";
import { useEvents } from "utilities/contexts/events/events-provider";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import { ConversationEventActions } from "models/constants/events/chat-event-actions";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import CasiChatBookviewRefAtom from "utilities/atoms/casi-chat-bookview-ref-atom";
import { useSetAtom } from "jotai";
import ChatPromptSuggestions from "organisms/chat-prompt-suggestions/chat-prompt-suggestions";
import useChatPrompts from "utilities/atoms/conversations/use-chat-prompts";
import PromptSuggestionRecord from "models/view-models/conversations/prompt-suggestions/prompt-suggestion-record";
import { CollectionUtils } from "utilities/collection-utils";
import useFeatureFlags from "utilities/hooks/use-feature-flags";

const BASE_CLASS_NAME = "c-chat-panel";
interface ChatPanelContentProps {
    context: ChatContextType;
    contextList: ReactNode[];
    applicationContext?: ConversationApplicationContextRecord;
}
const ChatPanelContent: React.FC<ChatPanelContentProps> = ({
    context,
    contextList,
    applicationContext,
}: ChatPanelContentProps) => {
    const {
        currentConversation,
        sendPrompt,
        lastAssistantMessageLengthLimited,
    } = useConversation();
    const { useCasiContext } = useFeatureFlags();
    const { clearConversation } = useConversationActions();
    const { createConversationEvent, createPromptSuggestionEvent } =
        useEvents();
    const { isOnline } = useNetworkInformation();
    const [prompt, setPrompt] = useState("");
    const [
        havePromptSuggestionsBeenDismissed,
        setHavePromptSuggestionsBeenDismissed,
    ] = useState(false);
    const [newChatStarted, setNewChatStarted] = useState(false);
    const [promptSuggestionEventPending, setPromptSuggestionEventPending] =
        useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const offlineClassName = !isOnline ? "-offline" : "";
    const setCasiChatBookviewRefAtom = useSetAtom(CasiChatBookviewRefAtom);
    const { promptSuggestions } = useChatPrompts(applicationContext);
    const arePromptSuggestionsVisible =
        CollectionUtils.isEmpty(currentConversation?.messages) &&
        !havePromptSuggestionsBeenDismissed;

    useEffect(() => {
        setCasiChatBookviewRefAtom(containerRef);
    }, [setCasiChatBookviewRefAtom]);

    useEffect(() => {
        if (!promptSuggestionEventPending || !currentConversation?.id) {
            return;
        }
        createPromptSuggestionEvent(
            currentConversation.id,
            ConversationEventActions.PromptSuggestionClicked
        );
        setPromptSuggestionEventPending(false);
    }, [
        createPromptSuggestionEvent,
        currentConversation?.id,
        promptSuggestionEventPending,
    ]);

    const handleClearChat = () => {
        if (!isOnline) {
            return;
        }

        const conversationId = currentConversation?.id;
        if (conversationId) {
            createConversationEvent(
                conversationId,
                ConversationEventActions.BookViewHistoryCleared
            );
        }

        clearConversation();
        setPrompt("");
        setNewChatStarted(true);
        setHavePromptSuggestionsBeenDismissed(false);
    };

    const handleContextChipClickEvent = () => {
        if (currentConversation?.id) {
            createConversationEvent(
                currentConversation.id,
                ConversationEventActions.ConversationContextChipClicked
            );
        }
    };

    const handleAutoPrompt = (prompt: PromptSuggestionRecord) => {
        setPromptSuggestionEventPending(true);
        sendPrompt(
            prompt.value,
            useCasiContext ? applicationContext : undefined
        );
    };

    return (
        <div className={`${BASE_CLASS_NAME} ${offlineClassName}`}>
            <div className={`${BASE_CLASS_NAME}__header`}>
                <Tooltip
                    disabled={isOnline}
                    content={<ChatOfflineTooltipContent />}
                    durationInMs={0}
                    hideOnClick={true}
                    placement={TooltipPlacement.AutoStart}>
                    <Button
                        cssClassName={`${BASE_CLASS_NAME}__button`}
                        style={ButtonStyles.Text}
                        onClick={handleClearChat}
                        aria-disabled={!isOnline}>
                        <Icon
                            cssClassName={`${BASE_CLASS_NAME}__icon`}
                            type={Icons.AutoRenew}
                        />
                        <Paragraph size={ParagraphSizes.XSmall}>
                            Clear
                        </Paragraph>
                    </Button>
                </Tooltip>
            </div>

            <div className={`${BASE_CLASS_NAME}__content`} ref={containerRef}>
                <ChatModalResults
                    containerRef={containerRef}
                    isBookview={true}
                    applicationContext={applicationContext}
                    includeConversationId={
                        // Inlcuding the conversationId will load the conversation in bookview when navigating to a citation
                        applicationContext?.currentView?.type === "Notebooks"
                    }
                />
                <div className={`${BASE_CLASS_NAME}__prompt-wrapper`}>
                    {!lastAssistantMessageLengthLimited &&
                        isOnline &&
                        arePromptSuggestionsVisible && (
                            <ChatPromptSuggestions
                                promptSuggestions={promptSuggestions}
                                onClick={handleAutoPrompt}
                                onDismiss={() =>
                                    setHavePromptSuggestionsBeenDismissed(true)
                                }
                            />
                        )}
                    <ChatContextChip
                        onClick={handleContextChipClickEvent}
                        context={context}
                        contextList={contextList}
                    />
                    <ChatPrompt
                        newChatStarted={newChatStarted}
                        prompt={prompt}
                        handleSetPrompt={setPrompt}
                        applicationContext={applicationContext}
                    />
                </div>
            </div>
        </div>
    );
};

export default ChatPanelContent;
